import { getSessionOrganizationId } from '../../../../shell/session/sessionOrganization';
import { getCatalogRamSnapshotCache } from '../../../../catalog/infra/catalogRamSnapshotCache';
import { buildCsvFromRows } from '../../../../shared/csv-core/infra/buildCsv'
import { CSV_IMPORT_MAX_ROWS } from '../../../../shared/csv-core/domain/csvConstants'
import { CSV_STOCK_SNAPSHOT_IMPORT_MESSAGE } from '../../../../shared/csv-core/domain/csvUiCopy'
import { parseCsvText, rowToRecord } from '../../../../shared/csv-core/infra/parseCsv';
import { normalizeProductCode } from '../../../../catalog/domain/catalogValidation';
import { receiveStock } from '../operations/stockAdjustmentsWrite';
import { findStockInWebCache } from '../operations/stockAdjustmentsRead';
import { findPhysicalProductBySkuInWebCache } from './stockCsvLookup';
import {
  looksLikeStockSnapshotExport,
  STOCK_CSV_MOTIVO_LABELS,
  STOCK_CSV_REQUIRED_IMPORT_HEADERS,
  type StockCsvMotivo,
} from '../../domain/csv/stockCsvSchemas';
import {
  parseStockCsvRowValues,
  type StockCsvParsedRow,
} from '../../domain/csv/stockCsvRowParse';

export { findPhysicalProductBySkuInWebCache };

export type StockCsvPreviewRow = {
  rowNumber: number;
  status: 'ok' | 'skip' | 'error';
  action: string;
  sku: string;
  detail: string;
  message?: string;
};

export type StockCsvPreviewResult = {
  totalRows: number;
  okCount: number;
  skipCount: number;
  errorCount: number;
  rows: StockCsvPreviewRow[];
  blocked?: boolean;
  blockMessage?: string;
};

export type StockCsvCommitResult = {
  applied: number;
  skipped: number;
  failed: number;
  errors: Array<{ rowNumber: number; message: string }>;
};

async function findPhysicalProductBySku(
  sku: string
): Promise<{ id: string; name: string; unitOfMeasure: string | null } | null> {
  const organizationId = await getSessionOrganizationId();
  if (!organizationId) return null;
  const normalized = normalizeProductCode(sku);
  if (!normalized) return null;

  // Web ram-only: SQLite es un stub vacío. El catálogo ya está en el snapshot en memoria.
  return findPhysicalProductBySkuInWebCache(normalized, organizationId);
}

async function getStockRow(
  branchId: string,
  productId: string
): Promise<{ id: string; quantity: number; avgCost: number; minQuantity: number } | null> {
  const existing = findStockInWebCache(productId, branchId);
        if (!existing) return null;
        return {
          id: existing.id,
          quantity: existing.quantity,
          avgCost: existing.avg_cost,
          minQuantity: existing.min_quantity,
        };
}

type SupplierCandidate = { id: string; name: string; code: string | null };

let supplierCandidatesCache: { orgId: string; candidates: SupplierCandidate[] } | null = null;

async function loadSupplierCandidates(): Promise<SupplierCandidate[]> {
  const orgId = await getSessionOrganizationId();
  if (!orgId) return [];
  if (supplierCandidatesCache?.orgId === orgId) return supplierCandidatesCache.candidates;
  const candidates = (getCatalogRamSnapshotCache()?.suppliers ?? [])
          .filter((s) => {
            if (s.deleted_at) return false;
            if (s.is_active === false) return false;
            if (s.organization_id && String(s.organization_id) !== orgId) return false;
            return Boolean(s.id);
          })
          .map((s) => ({
            id: String(s.id),
            name: String(s.name ?? ''),
            code: s.code != null && String(s.code).trim() !== '' ? String(s.code) : null,
          }));
        supplierCandidatesCache = { orgId, candidates };
        return candidates;
}

/** Resuelve el proveedor por id interno, código o nombre (exacto, case-insensitive). */
function resolveSupplierId(
  raw: string | undefined,
  candidates: SupplierCandidate[]
): { id: string | null; error?: string } {
  const value = raw?.trim();
  if (!value) return { id: null };
  const key = value.toLowerCase();

  const byId = candidates.filter((c) => c.id.trim().toLowerCase() === key);
  if (byId.length === 1) return { id: byId[0].id };

  const byCode = candidates.filter((c) => c.code != null && c.code.trim().toLowerCase() === key);
  if (byCode.length === 1) return { id: byCode[0].id };
  if (byCode.length > 1) return { id: null, error: `Varios proveedores con código "${value}".` };

  const byName = candidates.filter((c) => c.name.trim().toLowerCase() === key);
  if (byName.length === 1) return { id: byName[0].id };
  if (byName.length > 1) return { id: null, error: `Varios proveedores con nombre "${value}".` };

  return { id: null, error: `No encontramos el proveedor "${value}".` };
}

function requireStockHeaders(headers: string[]): string | null {
  const missing = STOCK_CSV_REQUIRED_IMPORT_HEADERS.filter((h) => !headers.includes(h));
  if (missing.length > 0) {
    return `Faltan estas columnas: ${missing.join(', ')}.`;
  }
  return null;
}

function motivoDetailLabel(reason: StockCsvMotivo): string {
  return STOCK_CSV_MOTIVO_LABELS[reason];
}

export async function previewStockCsvImport(
  branchId: string,
  csvText: string
): Promise<StockCsvPreviewResult> {
  if (!branchId) {
    return {
      totalRows: 0,
      okCount: 0,
      skipCount: 0,
      errorCount: 1,
      rows: [
        {
          rowNumber: 1,
          status: 'error',
          action: 'sucursal',
          sku: '',
          detail: 'Seleccioná una sucursal.',
          message: 'Seleccioná una sucursal.',
        },
      ],
      blocked: true,
      blockMessage: 'Seleccioná una sucursal para importar stock.',
    };
  }

  const parsed = parseCsvText(csvText);
  if (looksLikeStockSnapshotExport(parsed.headers)) {
    return {
      totalRows: 0,
      okCount: 0,
      skipCount: 0,
      errorCount: 1,
      rows: [
        {
          rowNumber: 1,
          status: 'error',
          action: 'encabezado',
          sku: '',
          detail: CSV_STOCK_SNAPSHOT_IMPORT_MESSAGE,
          message: CSV_STOCK_SNAPSHOT_IMPORT_MESSAGE,
        },
      ],
      blocked: true,
      blockMessage: CSV_STOCK_SNAPSHOT_IMPORT_MESSAGE,
    };
  }
  const headerError = requireStockHeaders(parsed.headers);
  if (headerError) {
    return {
      totalRows: 0,
      okCount: 0,
      skipCount: 0,
      errorCount: 1,
      rows: [
        {
          rowNumber: 1,
          status: 'error',
          action: 'encabezado',
          sku: '',
          detail: headerError,
          message: headerError,
        },
      ],
      blocked: true,
      blockMessage: headerError,
    };
  }

  if (parsed.rows.length > CSV_IMPORT_MAX_ROWS) {
    const msg = `El archivo tiene más de ${CSV_IMPORT_MAX_ROWS} filas.`;
    return {
      totalRows: parsed.rows.length,
      okCount: 0,
      skipCount: 0,
      errorCount: 1,
      rows: [
        {
          rowNumber: 1,
          status: 'error',
          action: 'límite',
          sku: '',
          detail: msg,
          message: msg,
        },
      ],
      blocked: true,
      blockMessage: msg,
    };
  }

  const supplierCandidates = await loadSupplierCandidates();

  const rows: StockCsvPreviewRow[] = [];
  let okCount = 0;
  const skipCount = 0;
  let errorCount = 0;

  for (let i = 0; i < parsed.rows.length; i += 1) {
    const rowNumber = i + 2;
    const record = rowToRecord(parsed.headers, parsed.rows[i]);
    const skuRaw = record.sku?.trim();
    if (!skuRaw) {
      rows.push({
        rowNumber,
        status: 'error',
        action: 'validar',
        sku: '',
        detail: 'SKU vacío',
        message: 'Falta el SKU.',
      });
      errorCount += 1;
      continue;
    }

    const sku = normalizeProductCode(skuRaw);
    const product = await findPhysicalProductBySku(sku);
    if (!product) {
      rows.push({
        rowNumber,
        status: 'error',
        action: 'validar',
        sku,
        detail: 'Producto no encontrado',
        message: 'No hay un producto físico con ese SKU.',
      });
      errorCount += 1;
      continue;
    }

    const stock = await getStockRow(branchId, product.id);
    const parsedRow = parseStockCsvRowValues(record, {
      hasExistingStock: stock != null,
      unitOfMeasure: product.unitOfMeasure,
    });
    if (!parsedRow.ok) {
      rows.push({
        rowNumber,
        status: 'error',
        action: 'validar',
        sku,
        detail: String(record.cantidad ?? ''),
        message: parsedRow.error,
      });
      errorCount += 1;
      continue;
    }

    const supplierMatch = resolveSupplierId(record.proveedor, supplierCandidates);
    if (record.proveedor?.trim() && !supplierMatch.id) {
      rows.push({
        rowNumber,
        status: 'error',
        action: 'validar',
        sku,
        detail: record.proveedor.trim(),
        message: supplierMatch.error ?? 'No encontramos el proveedor.',
      });
      errorCount += 1;
      continue;
    }

    const { quantity, reason } = parsedRow.value;
    const currentQty = stock?.quantity ?? 0;
    rows.push({
      rowNumber,
      status: 'ok',
      action: 'ingreso',
      sku,
      detail: `${currentQty} → ${currentQty + quantity} (+${quantity} · ${motivoDetailLabel(reason)})`,
    });
    okCount += 1;
  }

  return {
    totalRows: parsed.rows.length,
    okCount,
    skipCount,
    errorCount,
    rows,
  };
}

export async function commitStockCsvImport(
  branchId: string,
  csvText: string,
  opts?: { performedByUserId?: string | null }
): Promise<StockCsvCommitResult> {
  const preview = await previewStockCsvImport(branchId, csvText);
  if (preview.blocked) {
    throw new Error(preview.blockMessage ?? 'No pudimos importar el archivo.');
  }

  const parsed = parseCsvText(csvText);
  const supplierCandidates = await loadSupplierCandidates();
  const errors: Array<{ rowNumber: number; message: string }> = [];
  let applied = 0;
  let skipped = 0;
  let failed = 0;

  for (let i = 0; i < parsed.rows.length; i += 1) {
    const rowNumber = i + 2;
    const previewRowResult = preview.rows.find((r) => r.rowNumber === rowNumber);
    if (!previewRowResult || previewRowResult.status === 'error') {
      if (previewRowResult?.message) {
        errors.push({ rowNumber, message: previewRowResult.message });
      }
      failed += 1;
      continue;
    }
    if (previewRowResult.status === 'skip') {
      skipped += 1;
      continue;
    }

    const record = rowToRecord(parsed.headers, parsed.rows[i]);
    const sku = normalizeProductCode(record.sku?.trim() ?? '');

    try {
      const product = await findPhysicalProductBySku(sku);
      if (!product) throw new Error('Producto no encontrado.');

      const stock = await getStockRow(branchId, product.id);
      const parsedRow = parseStockCsvRowValues(record, {
        hasExistingStock: stock != null,
        unitOfMeasure: product.unitOfMeasure,
      });
      if (!parsedRow.ok) throw new Error(parsedRow.error);

      const supplierMatch = resolveSupplierId(record.proveedor, supplierCandidates);
      if (record.proveedor?.trim() && !supplierMatch.id) {
        throw new Error(supplierMatch.error ?? 'No encontramos el proveedor.');
      }

      const values: StockCsvParsedRow = parsedRow.value;
      // Costo: si no vino en el CSV, conservar el promedio actual (no diluirlo a 0).
      const unitCost = values.unitCost ?? stock?.avgCost ?? 0;
      // Mínimo: si no vino, conservar el actual para no resetearlo a 0.
      const minQuantity = values.minQuantity ?? stock?.minQuantity ?? 0;

      await receiveStock({
        branchId,
        product: { id: product.id, name: product.name },
        quantity: values.quantity,
        unitCost,
        minQuantity,
        expiresAt: values.expiresAt,
        lotNumber: values.lotNumber,
        reason: values.reason,
        performedByUserId: opts?.performedByUserId ?? null,
        supplierId: supplierMatch.id,
      });

      applied += 1;
    } catch (e) {
      failed += 1;
      errors.push({
        rowNumber,
        message: e instanceof Error ? e.message : 'No se pudo aplicar la fila.',
      });
    }
  }

  return { applied, skipped, failed, errors };
}

export function buildStockImportErrorsCsv(
  errors: Array<{ rowNumber: number; message: string }>
): string {
  return buildCsvFromRows(
    ['fila', 'mensaje'],
    errors.map((e) => [String(e.rowNumber), e.message])
  );
}
